// NSARA Stores V2 — onglet "Paramètres" (section 16).
//
// Sépare clairement les réglages métier (identité, coordonnées, état) de
// l'apparence (onglet Apparence).
//
// ÉTAT: la colonne shops.active existe déjà (sql/0003) et est réellement
// appliquée côté serveur — une boutique avec active=false renvoie 404 sur sa
// vitrine (backend/routes/publicShops.js) et 409 SHOP_INACTIVE sur une
// commande publique (backend/routes/publicProducts.js). Le basculement
// Active / En pause est donc une fonctionnalité réelle, pas décorative.
//
// SUPPRESSION: volontairement NON implémentée. Il n'existe aucune route
// DELETE /api/shops/:id, et pour cause: orders.shop_id est déclaré
// "on delete cascade" (sql/0003) tandis que products.shop_id est
// "on delete set null" (sql/0002). Supprimer une boutique détruirait donc
// définitivement ses commandes et transformerait ses produits en produits sans
// boutique (vendables depuis n'importe quelle boutique) — une perte de données
// commerciales et comptables irréversible. La section 16 demande explicitement
// de ne pas permettre une suppression dangereuse et de respecter les
// protections existantes: le besoin (suppression sûre + archivage) est remonté
// dans le rapport final avec la protection minimale requise, pas contourné ici.
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Power } from 'lucide-react';
import { updateShop } from '../../services/apiClient';

export default function StoreSettingsTab({ shop, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setForm({
      name: shop?.name || '',
      description: shop?.description || '',
      phone: shop?.phone || '',
      email: shop?.email || '',
      address: shop?.address || ''
    });
    setError('');
    setNotice('');
  }, [shop]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving || !form?.name.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { shop: saved } = await updateShop(shop.id, {
        name: form.name.trim(),
        description: form.description.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim()
      });
      setNotice('Paramètres enregistrés.');
      onSaved?.(saved);
      window.setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleState = async () => {
    if (toggling) return;
    setToggling(true);
    setError('');
    setNotice('');
    try {
      const nextActive = shop.active === false;
      const { shop: saved } = await updateShop(shop.id, { active: nextActive });
      setNotice(nextActive
        ? 'Boutique réactivée : la vitrine est de nouveau en ligne.'
        : 'Boutique mise en pause : vitrine et commandes en ligne suspendues.');
      onSaved?.(saved);
    } catch (err) {
      setError(err?.message || 'Changement d’état impossible.');
    } finally {
      setToggling(false);
    }
  };

  if (!form) return null;
  const paused = shop.active === false;

  return (
    <div className="stores-section">
      <div className="stores-section-head">
        <div className="stores-head-text">
          <h2 className="stores-section-title">Paramètres</h2>
          <p className="stores-section-sub">Les réglages métier de cette boutique.</p>
        </div>
      </div>

      {error && <p className="stores-alert stores-alert--error" role="alert">{error}</p>}
      {notice && <p className="stores-alert stores-alert--success" role="status">{notice}</p>}

      <form className="stores-panel" onSubmit={submit} noValidate>
        <h3 className="stores-panel-title">Informations de la boutique</h3>
        <div className="stores-field" style={{ marginBottom: 12 }}>
          <label className="stores-label" htmlFor="settings-name">Nom *</label>
          <input id="settings-name" className="stores-input" value={form.name} maxLength={200} disabled={saving} aria-required="true" onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div className="stores-field" style={{ marginBottom: 16 }}>
          <label className="stores-label" htmlFor="settings-description">Description</label>
          <textarea id="settings-description" className="stores-input stores-textarea" value={form.description} maxLength={500} rows={2} disabled={saving} onChange={(event) => setForm({ ...form, description: event.target.value })} />
        </div>

        <h3 className="stores-panel-title">Coordonnées (facultatif)</h3>
        <div className="stores-field-row" style={{ marginBottom: 12 }}>
          <div className="stores-field">
            <label className="stores-label" htmlFor="settings-phone">Téléphone</label>
            <input id="settings-phone" className="stores-input" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} maxLength={40} disabled={saving} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
          </div>
          <div className="stores-field">
            <label className="stores-label" htmlFor="settings-email">Email</label>
            <input id="settings-email" className="stores-input" type="email" autoComplete="email" value={form.email} maxLength={200} disabled={saving} onChange={(event) => setForm({ ...form, email: event.target.value })} />
          </div>
        </div>
        <div className="stores-field">
          <label className="stores-label" htmlFor="settings-address">Adresse</label>
          <input id="settings-address" className="stores-input" value={form.address} maxLength={300} disabled={saving} onChange={(event) => setForm({ ...form, address: event.target.value })} />
        </div>

        <div className="stores-form-actions" style={{ marginTop: 16 }}>
          <button type="submit" className="stores-btn stores-btn--primary" disabled={saving || !form.name.trim()}>
            {saving && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>

      <div className="stores-panel">
        <h3 className="stores-panel-title">
          <Power size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />État
        </h3>
        <p className="stores-detail-value" style={{ marginBottom: 12 }}>
          {paused
            ? 'Boutique en pause : sa vitrine publique renvoie une page indisponible et les commandes en ligne sont refusées. La gestion interne (produits, stock, ventes) reste accessible.'
            : 'Boutique active : sa vitrine publique est en ligne et accepte les commandes.'}
        </p>
        <div className="stores-form-actions">
          <button type="button" className={paused ? 'stores-btn stores-btn--primary' : 'stores-btn stores-btn--outline'} disabled={toggling} onClick={toggleState}>
            {toggling && <Loader2 size={15} aria-hidden="true" className="stores-spin" />}
            {paused ? 'Réactiver la boutique' : 'Mettre en pause'}
          </button>
          <span className={paused ? 'stores-badge stores-badge--paused' : 'stores-badge stores-badge--active'}>{paused ? 'En pause' : 'Active'}</span>
        </div>
      </div>

      <div className="stores-panel stores-danger-zone">
        <h3 className="stores-panel-title">
          <AlertTriangle size={15} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 6 }} />Zone sensible
        </h3>
        <p className="stores-detail-value" style={{ marginBottom: 12 }}>
          La suppression d’une boutique n’est pas disponible. En l’état du schéma, elle détruirait définitivement
          les commandes de cette boutique et détacherait ses produits (qui deviendraient vendables depuis n’importe
          quelle boutique) — une perte de données commerciales et comptables irréversible.
        </p>
        <p className="stores-hint">
          Pour retirer une boutique de votre vitrine sans rien perdre, mettez-la en pause ci-dessus,
          ou masquez ses produits un par un dans l’onglet Produits.
        </p>
        <div className="stores-form-actions" style={{ marginTop: 12 }}>
          <button type="button" className="stores-btn stores-btn--danger" disabled aria-describedby="delete-shop-hint">
            Supprimer la boutique (indisponible)
          </button>
          <span className="stores-hint" id="delete-shop-hint">
            Protégé tant qu’une suppression sûre avec archivage n’existe pas côté serveur.
          </span>
        </div>
      </div>
    </div>
  );
}
